class CustomSizeMeasurements extends HTMLElement {
  #abortController = new AbortController();
  #isFormBlocked = false;

  connectedCallback() {
    const { signal } = this.#abortController;
    const eventScope = this.#getEventScope();

    eventScope.addEventListener('variant:update', this.#updateVisibility, { signal });
    eventScope.addEventListener('change', this.#updateVisibility, { signal });
    this.addEventListener('input', this.#handleValueInput, { signal });
    this.addEventListener('change', this.#handleTermsChange, { signal });
    this.addEventListener('click', this.#handleUnitToggle, { signal });

    const form = this.#getForm();
    form?.addEventListener('submit', this.#handleFormSubmit, { signal });

    this.#initializeUnitSwitch();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #getEventScope() {
    return (
      this.closest('.shopify-section, featured-product-information, quick-add-dialog, product-card') || document
    );
  }

  #getVariantPicker() {
    const sectionId = this.dataset.sectionId;

    if (sectionId) {
      const escapedSectionId = CSS.escape(sectionId);
      const sectionScopedPicker = document.querySelector(`variant-picker[data-section-id="${escapedSectionId}"]`);

      if (sectionScopedPicker) return sectionScopedPicker;
    }

    const localPicker = this.#getEventScope().querySelector('variant-picker');
    if (localPicker) return localPicker;

    return document.querySelector('variant-picker');
  }

  #getForm() {
    const formId = this.dataset.formId;
    if (formId) {
      const formById = document.getElementById(formId);
      if (formById instanceof HTMLFormElement) return formById;
    }

    const localForm = this.closest('form');
    if (localForm instanceof HTMLFormElement) return localForm;

    return null;
  }

  #setFormBlockedState(isBlocked) {
    this.#isFormBlocked = isBlocked;

    const form = this.#getForm();
    if (!form) return;

    form.classList.toggle('custom-size-incomplete', isBlocked);
  }

  #handleValueInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-custom-size-value-input]')) return;

    this.#syncHiddenInputs();
    this.#updateVisibility();
  };

  #handleTermsChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-custom-size-terms-checkbox]')) return;

    this.#updateVisibility();
  };

  #handleUnitToggle = (event) => {
    const toggleButton = event.target.closest('[data-custom-size-unit-toggle]');
    if (!(toggleButton instanceof HTMLElement)) return;

    event.preventDefault();

    const currentUnit = this.#getCurrentUnit();
    const nextUnit = currentUnit === 'cm' ? 'inch' : 'cm';

    this.#setCurrentUnit(nextUnit);
    this.#syncHiddenInputs();
  };

  #handleFormSubmit = (event) => {
    this.#syncHiddenInputs();

    if (!this.#isFormBlocked) return;

    event.preventDefault();

    const firstInvalidInput = this.querySelector('[data-custom-size-fields] [data-custom-size-value-input]:invalid');
    if (firstInvalidInput instanceof HTMLInputElement) {
      firstInvalidInput.reportValidity();
      firstInvalidInput.focus();
      return;
    }

    const termsCheckbox = this.querySelector('[data-custom-size-terms-checkbox]');
    if (termsCheckbox instanceof HTMLInputElement && termsCheckbox.required && !termsCheckbox.checked) {
      termsCheckbox.reportValidity();
      termsCheckbox.focus();
    }
  };

  #initializeUnitSwitch() {
    const unitSwitch = this.querySelector('[data-custom-size-unit-switch]');

    if (!(unitSwitch instanceof HTMLElement)) return;

    if (unitSwitch.dataset.customSizeUnitSwitchInitialized === 'true') {
      return;
    }

    unitSwitch.dataset.customSizeUnitSwitchInitialized = 'true';
    this.#setCurrentUnit('cm');
  }

  #getCurrentUnit() {
    return this.dataset.currentUnit === 'inch' ? 'inch' : 'cm';
  }

  #setCurrentUnit(unit) {
    const normalizedUnit = unit === 'inch' ? 'inch' : 'cm';
    const isInch = normalizedUnit === 'inch';
    const unitSwitch = this.querySelector('[data-custom-size-unit-switch]');

    this.dataset.currentUnit = normalizedUnit;

    if (unitSwitch instanceof HTMLElement) {
      unitSwitch.dataset.currentUnit = normalizedUnit;
      unitSwitch.classList.toggle('is-inch', isInch);

      const toggleButton = unitSwitch.querySelector('[data-custom-size-unit-toggle]');
      if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-checked', isInch.toString());
      }
    }

    const suffixes = this.querySelectorAll('[data-custom-size-input-suffix]');
    for (const suffix of suffixes) {
      if (!(suffix instanceof HTMLElement)) continue;
      suffix.textContent = normalizedUnit;
    }
  }

  #syncHiddenInputs(shouldShow = this.dataset.visible === 'true') {
    const unit = this.#getCurrentUnit();
    const fields = this.querySelectorAll('[data-custom-size-field]');

    for (const field of fields) {
      if (!(field instanceof HTMLElement)) continue;

      const valueInput = field.querySelector('[data-custom-size-value-input]');
      const hiddenInput = field.querySelector('[data-custom-size-hidden-input]');

      if (!(valueInput instanceof HTMLInputElement) || !(hiddenInput instanceof HTMLInputElement)) {
        continue;
      }

      const rawValue = valueInput.value.trim();
      const hasValue = rawValue !== '';
      const shouldEnableHiddenInput = shouldShow && hasValue;

      hiddenInput.disabled = !shouldEnableHiddenInput;
      hiddenInput.value = shouldEnableHiddenInput ? `${rawValue} ${unit}` : '';
    }
  }

  #extractOptionNameFromLegend(legend) {
    if (!(legend instanceof HTMLElement)) return '';

    const clone = legend.cloneNode(true);
    const swatchValue = clone.querySelector('.variant-option__swatch-value');
    if (swatchValue) swatchValue.remove();

    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  #getSelectedSizeValue() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return '';

    const selects = Array.from(variantPicker.querySelectorAll('select[name^="options["]'));
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;

      const optionMatch = select.name.match(/^options\[(.+)\]$/);
      const optionName = optionMatch ? optionMatch[1] : '';
      if (!optionName.toLowerCase().includes('size')) continue;

      return (select.value || '').trim();
    }

    const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset.variant-option'));
    for (const fieldset of fieldsets) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const legend = fieldset.querySelector('legend');
      const optionName = this.#extractOptionNameFromLegend(legend);
      if (!optionName.toLowerCase().includes('size')) continue;

      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) {
        return (checkedInput.value || '').trim();
      }
    }

    return '';
  }

  #updateVisibility = () => {
    const selectedSizeValue = this.#getSelectedSizeValue();
    const shouldShow = selectedSizeValue.toLowerCase() === 'custom';
    const fieldContainer = this.querySelector('[data-custom-size-fields]');

    if (!(fieldContainer instanceof HTMLElement)) return;

    this.hidden = !shouldShow;
    fieldContainer.hidden = !shouldShow;
    this.dataset.visible = shouldShow ? 'true' : 'false';

    const inputs = fieldContainer.querySelectorAll('[data-custom-size-value-input]');
    let hasIncompleteField = false;

    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const isRequiredField = input.dataset.required !== 'false';
      input.disabled = !shouldShow;
      input.required = shouldShow && isRequiredField;

      if (shouldShow && isRequiredField && input.value.trim() === '') {
        hasIncompleteField = true;
      }
    }

    const termsCheckbox = this.querySelector('[data-custom-size-terms-checkbox]');
    let hasIncompleteTerms = false;

    if (termsCheckbox instanceof HTMLInputElement) {
      termsCheckbox.disabled = !shouldShow;
      termsCheckbox.required = shouldShow;
      termsCheckbox.setAttribute('aria-required', shouldShow.toString());

      if (shouldShow && !termsCheckbox.checked) {
        hasIncompleteTerms = true;
      }
    }

    this.#syncHiddenInputs(shouldShow);
    this.#setFormBlockedState(shouldShow && (hasIncompleteField || hasIncompleteTerms));
  };
}

if (!customElements.get('custom-size-measurements')) {
  customElements.define('custom-size-measurements', CustomSizeMeasurements);
}
